import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addUser, updateUser, getAllUsers, getAllUsersAcrossTenants } from "features/appUsers/appUsers.slice";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Links } from "utils/pageLinkNames";
import { isEmail, isPhoneNumber } from "utils/utilFunctions";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import { buildRegistrationApprovedMessage } from "utils/registrationRequestMessaging";
import { removeRegistrationRequest } from "features/auth/auth.slice";

import { LoadingButton } from "@mui/lab";
import {
  Typography,
  Button,
  Stack,
  TextField,
  Tooltip,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
} from "@mui/material";
import { styled } from "@mui/system";

const CustomPhoneInput = styled(PhoneInput)(({ theme }) => ({
  "& .PhoneInputInput": {
    height: "51px",
    padding: "15px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "5px",
    width: "100%",
    fontSize: "15px",
    backgroundColor: theme.palette.mode === "dark" ? "#333333" : "#ffffff",
    color: theme.palette.mode === "dark" ? "white" : "black",
    "&:focus": {
      borderColor: theme.palette.mode === "dark" ? "black" : "#006a78",
      borderWidth: "2px !important",
      borderStyle: "solid !important",
      outline: "none",
    },
  },
  "& .PhoneInputCountryIcon": {
    height: "29px",
    width: "40px",
  },
}));

const AddEditUser = (props) => {
  const { id } = useParams();
  const theme = useTheme();
  const { mode } = useSelector((state) => state.globalState);

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlertBox, setShowAlertBox] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    department: "",
    userRole: "",
    userName: "",
    password: "",
    confirmPassword: "",
    enabled: true,
    inactiveReason: "",
    responsibleForFacilitiesTickets: false,
    resetPassword: !id, // Set to true for new users, false for editing
  });

  const { appConstants } = useSelector((state) => state.service);
  const { allUsersAcrossTenants } = useSelector((state) => state.appUsers);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllUsersAcrossTenants());
  }, [dispatch]);

  const toCamelCase = (str) => {
    if (!str) return str;
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const generateUsername = (name, existingUsers = [], currentUserId = null) => {
    if (!name) return "";

    const baseUsername = name
      .trim()
      .toLowerCase()
      .replace(/[\s,]+/g, "")
      .slice(0, 10);
    let finalUsername = baseUsername;
    let counter = 1;

    const existingUsernames = existingUsers
      .filter((user) => user.userId !== parseInt(currentUserId))
      .map((user) => user.userName.toLowerCase().trim());

    while (existingUsernames.includes(finalUsername)) {
      const suffix = counter.toString();
      finalUsername = baseUsername.slice(0, 10 - suffix.length) + suffix;
      counter++;
    }

    return finalUsername;
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const camelCaseName = toCamelCase(name);
    const username = generateUsername(name, allUsersAcrossTenants, id);

    clearError("name");
    clearError("userName");

    setFormData((prev) => ({
      ...prev,
      name: camelCaseName,
      userName: username,
    }));
  };

  const handleMobileNumberChange = (phone) => {
    clearError("mobileNumber");
    clearError("password");
    clearError("confirmPassword");

    const parsed = parsePhoneNumberFromString(phone || "");
    const nationalNumber = parsed ? parsed.nationalNumber : (phone || "").replace(/\D/g, "");

    setFormData((prev) => ({
      ...prev,
      mobileNumber: phone,
      ...((!id || prev.resetPassword) && {
        password: nationalNumber.slice(0, 6),
        confirmPassword: nationalNumber.slice(0, 6),
      }),
    }));
  };

  const clearError = (fieldName) => {
    setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const parseInactiveReasonFromInfo = (info) => {
    if (!info) return "";
    try {
      const infoMap = typeof info === "string" ? JSON.parse(info) : info;
      return infoMap?.inactiveReason ? String(infoMap.inactiveReason).trim() : "";
    } catch {
      return "";
    }
  };

  const parseResponsibleForFacilitiesTicketsFromInfo = (info) => {
    if (!info) return false;
    try {
      const infoMap = typeof info === "string" ? JSON.parse(info) : info;
      return !!infoMap?.responsible_for_facilities_tickets;
    } catch {
      return false;
    }
  };

  const maintenanceSupervisorRole = appConstants?.allTypes?.CALMAN_USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR;
  const isMaintenanceSupervisorRole = formData.userRole === maintenanceSupervisorRole;

  const getUserIdFromApiResponse = (res) => {
    const payload = res?.payload;
    const data = payload?.data ?? payload;
    if (!data || typeof data !== "object") return null;
    return data.id ?? data.userId ?? data.user_id ?? null;
  };

  const resolveDepartmentId = (department) => {
    if (department == null || department === "") return null;
    if (typeof department === "object" && department.id != null) return department.id;
    if (typeof department === "string") {
      const departments = appConstants?.departments || [];
      const match = departments.find((d) => String(d?.name || "").trim() === String(department).trim());
      return match?.id ?? null;
    }
    return null;
  };

  const completeUserCreate = (createdUserId, notifyPayload) => {
    const userId = createdUserId != null ? String(createdUserId) : "";
    if (!userId || userId === "undefined" || userId === "null") {
      toast.error("User was created but could not open the profile. Please check the Users list.");
      return;
    }

    // Navigate immediately so the user is not blocked by list refreshes / registration cleanup.
    navigate(`/${Links.EDIT_USER}/${userId}`, {
      replace: true,
      state: { accountCreatedNotify: notifyPayload },
    });
    toast.success("User added successfully!", { autoClose: 1500 });

    const registrationRequestUuid = props?.registrationRequestUuid;
    void (async () => {
      try {
        if (registrationRequestUuid) {
          await dispatch(removeRegistrationRequest(registrationRequestUuid)).unwrap();
        }
      } catch {
        // Best-effort cleanup; user was already created successfully.
      }
      dispatch(getAllUsers());
      dispatch(getAllUsersAcrossTenants());
    })();
  };

  const handleSubmit = () => {
    const errorValidations = {
      name: formData.name ? "" : "Please enter name!",
      userName: formData.userName ? "" : "Please enter user name!",
      userRole: formData.userRole ? "" : "Please enter user role!",
      department: formData.department ? "" : "Please enter department!",
      email: "",
      mobileNumber: formData.mobileNumber ? "" : "Please enter mobile number!",
    };

    if (!id && !formData.password) errorValidations.password = formData.password ? "" : "Please enter password!";
    if (!id && !formData.confirmPassword)
      errorValidations.confirmPassword = formData.confirmPassword ? "" : "Please enter confirmPassword!";
    if (!id && formData.password !== formData.confirmPassword) errorValidations.confirmPassword = "Password doesn't match!";
    if (id && formData.password)
      errorValidations.confirmPassword = formData.password !== formData.confirmPassword ? "Password doesn't match!" : "";
    if (Object.values(errorValidations).filter((item) => item).length) {
      setErrors(errorValidations);
      return toast.warn("Please enter required fields!");
    }
    if (formData.email && !isEmail(formData.email)) {
      errorValidations.email = "Please enter a valid email!";
      setErrors(errorValidations);
      return toast.warn("Please enter a valid email!");
    }
    if (formData.mobileNumber && !isPhoneNumber(formData.mobileNumber)) {
      errorValidations.mobileNumber = "Please enter a valid mobile number!";
      setErrors(errorValidations);
      return toast.warn("Please enter a valid mobile number!");
    }

    if (!formData.enabled) {
      const inactiveReason = (formData.inactiveReason ?? "").trim();
      if (!inactiveReason) {
        errorValidations.inactiveReason = "Reason is required when user is inactive";
        setErrors(errorValidations);
        return toast.warn("Please enter reason for inactive status!");
      }
    }

    const normalizedUsername = formData.userName.toLowerCase().trim();
    const currentUserId = id ? parseInt(id) : null;
    const userExists = allUsersAcrossTenants.some((user) => {
      return user.userName.toLowerCase().trim() === normalizedUsername && user.userId !== currentUserId;
    });
    if (userExists) {
      return toast.error("Username already exists!");
    } else {
      const departmentId = resolveDepartmentId(formData.department);
      if (departmentId == null) {
        setErrors((prev) => ({ ...prev, department: "Please enter department!" }));
        return toast.warn("Please enter department!");
      }
      formData.department = departmentId;
      delete formData.departmentId;
      delete formData.confirmPassword;
      delete formData.info;
      if (formData.email === "" || formData.email == null) delete formData.email;
      formData.resetPassword = formData.resetPassword ? true : false;
      if (!formData.enabled) {
        formData.inactiveReason = (formData.inactiveReason ?? "").trim();
      } else {
        delete formData.inactiveReason;
      }
      formData.responsible_for_facilities_tickets = isMaintenanceSupervisorRole
        ? !!formData.responsibleForFacilitiesTickets
        : false;
      delete formData.responsibleForFacilitiesTickets;
      if (id) {
        if (!formData.password) delete formData.password;
        formData.userId = parseInt(id);
        submitForm(updateUser, formData);
      } else submitForm(addUser, formData);
    }
  };

  const submitForm = (action, formData) => {
    if (formData.mobileNumber) {
      if (!formData.mobileNumber.startsWith("+")) {
        formData.mobileNumber = "+" + formData.mobileNumber;
      }
    }
    setIsSubmitting(true);
    let navigatedAway = false;
    dispatch(action(formData))
      .then(async (res) => {
        if (res && res.error) {
          toast.error("Something went wrong!");
          return;
        }
        const httpStatus = res?.payload?.status ?? res?.payload?.data?.status;
        const hasSuccessBody = res?.payload?.data != null;
        if (res && !res.error && hasSuccessBody && (httpStatus === 200 || httpStatus === undefined)) {
          const savedUserId = getUserIdFromApiResponse(res) ?? id ?? formData.userId;
          const isCreate = !id && !formData.userId;

          if (isCreate) {
            const name = formData?.name || "";
            const username = formData?.userName || "";
            const password = formData?.password || "";
            const message = buildRegistrationApprovedMessage({
              name,
              userName: username,
              password,
            });

            navigatedAway = true;
            completeUserCreate(savedUserId, {
              message,
              mobileNumber: formData?.mobileNumber || "",
              recipientName: name,
            });
            return;
          }

          const editUserId = savedUserId != null ? String(savedUserId) : "";
          if (!editUserId) {
            toast.error("Something went wrong!");
            return;
          }
          navigate(`/${Links.EDIT_USER}/${editUserId}`, { replace: true });
          dispatch(getAllUsers()).then(() => {
            dispatch(getAllUsersAcrossTenants());
            toast.success("User updated successfully!", { autoClose: 1500 });
            props.setEnableEditing?.(false);
          });
        } else {
          toast.error("Something went wrong!");
        }
      })
      .finally(() => {
        if (!navigatedAway) setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (props && props.initialFormData) {
      const initialFormData = { ...props.initialFormData };
      if (!id) initialFormData.enabled = true;

      // Normalize department prefill so Autocomplete renders a label (avoids [object Object]).
      let normalizedDepartment = initialFormData.department;
      const deptId =
        initialFormData?.departmentId ??
        normalizedDepartment?.id ??
        normalizedDepartment?.departmentId ??
        initialFormData?.department?.id ??
        initialFormData?.department?.departmentId;

      const departments = appConstants?.departments || [];
      if (departments.length) {
        let match = null;

        if (deptId != null && deptId !== "") {
          match = departments.find((d) => String(d?.id) === String(deptId)) || null;
        }

        // Fallback: sometimes user payload contains department as a plain name string.
        if (!match) {
          const deptNameCandidate =
            typeof initialFormData?.department === "string"
              ? initialFormData.department
              : initialFormData?.departmentName || normalizedDepartment?.name || normalizedDepartment?.label || "";
          const deptName = String(deptNameCandidate || "").trim();
          if (deptName) {
            match = departments.find((d) => String(d?.name || "").trim() === deptName) || null;
          }
        }

        if (match) {
          normalizedDepartment = { ...match, value: match?.name, label: match?.name };
        }
      }

      // Prefill password/confirm password (first 6 digits of national number, no country code)
      const parsed = parsePhoneNumberFromString(initialFormData.mobileNumber || "");
      const nationalNumber = parsed ? String(parsed.nationalNumber || "") : String(initialFormData.mobileNumber || "").replace(/\D/g, "");
      const six = nationalNumber.slice(0, 6);

      setFormData({
        ...initialFormData,
        department: normalizedDepartment,
        inactiveReason: parseInactiveReasonFromInfo(initialFormData.info),
        responsibleForFacilitiesTickets: parseResponsibleForFacilitiesTicketsFromInfo(initialFormData.info),
        resetPassword: !id,
        ...(!id && six ? { password: six, confirmPassword: six } : {}),
      });
    }
  }, [props?.initialFormData, id, appConstants?.departments]);

  const showInactiveReason = !formData.enabled;
  const showPasswordFields = !id || !!formData.resetPassword;

  const formFieldGridSx = {
    width: { xs: "100%", sm: 400 },
    maxWidth: { xs: "100%", sm: 400 },
  };

  const inlinePasswordFieldGridSx = {
    width: { xs: "100%", md: 280, lg: 300 },
    maxWidth: { xs: "100%", md: 280, lg: 300 },
    flexShrink: 0,
  };

  const checkboxLabelSx = {
    m: 0,
    ml: 0,
    pl: 0,
    alignItems: "center",
    "& .MuiCheckbox-root": {
      p: 0.5,
      ml: 0,
    },
  };

  const setPasswordCheckbox = id ? (
    <FormControlLabel
      sx={checkboxLabelSx}
      componentsProps={{ typography: { variant: "body2", fontSize: "0.8125rem" } }}
      control={
        <Checkbox
          size="small"
          checked={!!formData.resetPassword}
          onChange={(event) =>
            setFormData({
              ...formData,
              resetPassword: event.target.checked,
            })
          }
        />
      }
      label="Set password"
    />
  ) : null;

  const passwordVisibilityAdornment = (field) => (
    <InputAdornment position="end">
      <IconButton
        size="small"
        aria-label="toggle password visibility"
        onClick={() => setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }))}
        edge="end"
      >
        {showPassword[field] ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const renderUserTicketsAlert = () => {
    const userTickets = props?.serviceTickets
      ? props.serviceTickets.filter((tkt) => tkt.status === appConstants?.allTypes?.SERVICE_TICKET_STATUS?.ASSIGNED)?.map((item) => item.id)
      : [];

    return userTickets.length ? (
      <div>
        <Typography mt={1}>
          All the tickets <span style={{ fontWeight: "bold" }}> "Assigned" </span> to {formData?.name || ""} will be changed to
          <span style={{ fontWeight: "bold" }}> "Assign Technician" </span> status.
        </Typography>
        <Typography mt={1} fontWeight={"bold"}>
          Ticket(s) :
        </Typography>
        <div style={{ marginLeft: 10 }}>
          <Typography mt={1}>{userTickets?.join(", ")}</Typography>
        </div>
      </div>
    ) : (
      <Typography mt={1}>
        Currently no tickets were <span style={{ fontWeight: "bold" }}> "Assigned" </span> to {formData?.name || ""}.
      </Typography>
    );
  };
  return (
    <div>
      <div>
        <Dialog open={showAlertBox} onClose={() => setShowAlertBox(false)}>
          <DialogTitle fontWeight={"bold"}>Alert!</DialogTitle>
          <DialogContent>
            <Typography>You are going to disable the user.</Typography>

            {/* {renderUserTicketsAlert()} */}
          </DialogContent>
          <DialogActions sx={{ mb: 2 }}>
            <Button sx={{ height: "self" }} variant="contained" onClick={() => setShowAlertBox(false)}>
              Cancel
            </Button>
            <Button
              sx={{ height: "self" }}
              variant="contained"
              onClick={() => {
                setShowAlertBox(false);
                handleSubmit();
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <Stack direction={"row"} gap={2}>
        <Stack direction={"row"} gap={2} flexWrap={"wrap"} display={"flex"}>
          <TextField
            label="Name"
            value={formData.name}
            autoComplete="new-name"
            onChange={handleNameChange}
            error={errors && errors.name}
            helperText={errors.name}
            sx={{ minWidth: 400, maxWidth: 600 }}
          />
          <TextField
            label="User name"
            autoComplete="new-username"
            value={formData.userName}
            onChange={(event) => setFormData({ ...formData, userName: event.target.value })}
            error={errors && errors.userName}
            helperText={errors.userName}
            sx={{ minWidth: 400, maxWidth: 600 }}
          />
          <TextField
            label="Email"
            value={formData.email}
            autoComplete="new-email"
            onChange={(event) => setFormData({ ...formData, email: event.target.value })}
            error={errors && errors.email && !isEmail(formData.email)}
            helperText={errors.email}
            sx={{ minWidth: 400, maxWidth: 600 }}
          />
          <Box sx={{ minWidth: 400, maxWidth: 600 }}>
            <CustomPhoneInput
              international={true}
              defaultCountry="US"
              value={formData.mobileNumber}
              onChange={handleMobileNumberChange}
              countryCallingCodeEditable={false}
              withCountryCallingCode={false}
            />
            {errors.mobileNumber && (
              <Typography color="error" sx={{ fontSize: "0.65rem", mt: 1, ml: 1.5 }}>
                {errors.mobileNumber}
              </Typography>
            )}
          </Box>
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            autoComplete="new-role"
            onChange={(event, newValue) => {
              const isSupervisor = newValue === maintenanceSupervisorRole;
              setFormData({
                ...formData,
                userRole: newValue,
                ...(isSupervisor ? {} : { responsibleForFacilitiesTickets: false }),
              });
              clearError("userRole");
            }}
            value={formData.userRole || null}
            options={
              appConstants?.allTypes?.CALMAN_USER_ROLES
                ? Object.values(appConstants.allTypes.CALMAN_USER_ROLES).sort((a, b) => a.localeCompare(b))
                : []
            }
            sx={{ width: 400, maxWidth: 600 }}
            renderInput={(params) => <TextField {...params} label="Role" error={errors && errors.userRole} helperText={errors.userRole} />}
          />
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                department: newValue,
              });
              clearError("department");
            }}
            value={formData.department || null}
            autoComplete="new-department"
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            getOptionLabel={(option) => option?.label || option?.name || ""}
            options={
              Array.isArray(appConstants?.departments)
                ? appConstants.departments
                    .map((item) => ({ ...item, value: item.name, label: item.name }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                : []
            }
            sx={{ minWidth: 400, maxWidth: 600 }}
            renderInput={(params) => (
              <TextField {...params} label="Department" error={errors && errors.department} helperText={errors.department} />
            )}
          />

          <Stack spacing={1.5} sx={{ width: "100%", maxWidth: showPasswordFields ? 960 : 600 }}>
            {/* Checkboxes + password fields — one row on md+, stacks below md */}
            <Grid container spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
              <Grid item xs={12} md="auto" sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                <FormControlLabel
                  sx={checkboxLabelSx}
                  componentsProps={{ typography: { variant: "body2", fontSize: "0.8125rem" } }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!formData.enabled}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          enabled,
                          ...(enabled ? { inactiveReason: "" } : {}),
                        }));
                        if (enabled) clearError("inactiveReason");
                      }}
                    />
                  }
                  label={formData.enabled ? "Active" : "Inactive"}
                />
              </Grid>
              {id ? (
                <Grid item xs={12} md="auto" sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  {setPasswordCheckbox}
                </Grid>
              ) : null}

              {showPasswordFields ? (
                <>
                  <Grid item xs={12} md="auto" sx={inlinePasswordFieldGridSx}>
                    <TextField
                      size="small"
                      label="Password"
                      type={showPassword.password ? "text" : "password"}
                      value={formData.password || ""}
                      onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{ endAdornment: passwordVisibilityAdornment("password") }}
                      fullWidth
                      autoComplete="new-password"
                    />
                  </Grid>
                  <Grid item xs={12} md="auto" sx={inlinePasswordFieldGridSx}>
                    <TextField
                      size="small"
                      label="Confirm password"
                      type={showPassword.confirmPassword ? "text" : "password"}
                      value={formData.confirmPassword || ""}
                      onChange={(event) => setFormData({ ...formData, confirmPassword: event.target.value })}
                      InputProps={{ endAdornment: passwordVisibilityAdornment("confirmPassword") }}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      fullWidth
                      autoComplete="new-password"
                    />
                  </Grid>
                </>
              ) : null}
              {isMaintenanceSupervisorRole ? (
                <Grid item xs={12} md="auto" sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <FormControlLabel
                    sx={checkboxLabelSx}
                    componentsProps={{ typography: { variant: "body2", fontSize: "0.8125rem" } }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!formData.responsibleForFacilitiesTickets}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            responsibleForFacilitiesTickets: event.target.checked,
                          })
                        }
                      />
                    }
                    label="Responsible for Facility Tickets"
                  />
                </Grid>
              ) : null}
            </Grid>

            {/* Inactive reason */}
            {showInactiveReason ? (
              <Grid container spacing={0.6}>
                <Grid item xs={12} sm="auto" sx={formFieldGridSx}>
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    label="Inactive reason"
                    placeholder="Required"
                    value={formData.inactiveReason || ""}
                    onChange={(event) => {
                      setFormData({ ...formData, inactiveReason: event.target.value });
                      clearError("inactiveReason");
                    }}
                    error={!!errors.inactiveReason}
                    helperText={errors.inactiveReason}
                    required
                    fullWidth
                    sx={{
                      "& textarea": {
                        maxHeight: 96,
                        overflowY: "auto !important",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            ) : null}
          </Stack>
        </Stack>
        <Stack alignItems="flex-start" sx={{ pt: 1 }}>
          <Tooltip title="Save">
            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              sx={{ minWidth: 120 }}
              onClick={() => {
                if (!formData.enabled) {
                  const inactiveReason = (formData.inactiveReason ?? "").trim();
                  if (!inactiveReason) {
                    setErrors((prev) => ({
                      ...prev,
                      inactiveReason: "Reason is required when user is inactive",
                    }));
                    toast.warn("Please enter reason for inactive status!");
                    return;
                  }
                }
                const currentUserStatus = id ? allUsersAcrossTenants.find((user) => user.userId === parseInt(id)) : null;
                if (id && currentUserStatus?.enabled && !formData.enabled) {
                  setShowAlertBox(true);
                  return;
                }
                handleSubmit();
              }}
            >
              Save
            </LoadingButton>
          </Tooltip>
        </Stack>
      </Stack>
      <ToastContainer />
    </div>
  );
};

export default AddEditUser;
